import React from 'react';
import { Link } from 'react-router-dom';

interface AboutProps {
  theme?: 'light' | 'dark';
}

const About: React.FC<AboutProps> = ({ theme }) => {
  const accentColor = "#00CCEB";

  const linkStyle = {
    color: accentColor,
    textDecoration: 'none',
    fontWeight: 'bold'
  };

  const versionHistory = [
            {
            version: "4.4.1",
                      changes: [
                        "Bug fixes."
                      ]
                  },
            {
            version: "4.4",
                changes: [
                  "Setup Guide: In-app support for your own EQ and Multi-band Compressor preset based on hearing/preference questions or an audiogram - the guide is now available from the About menu.",
                  "Multi-band compressor: Factory presets like Clear Dialogue and De-Esser added, along with small UI improvements.",
                  "Bug fixes."
                ]
            },
            {
            version: "4.3",
                  changes: [
                    "Search function in the music library view",
                    "Media player now loads last played track on restart",
                    "Bug fixes"
                  ]
          },
          {
          version: "4.2",
                changes: [
                  "Increased possible dB ranges on EQ bands, pre-gain and balance (for subscribers).",
                  "iOS: Multi-band compressor added (similar to the one for Android subscribers).",
                  "Battery and stability improvements",
                  "Bug fixes"
                ]
          },
          {
              version: "4.1",
              changes: [
                "The 33 band EQ can now use the Fitted size config",
                "Added a UI effects setting to improve performance",
                "Bug fixes."
              ]
            },
      {
            version: "4.0 Android",
            changes: [
              "Multi-band compressor added for subscribers. Save your dual-sided compressor settings independently of the EQ presets.",
              "Language support added. 14 app translations selectable from Settings / Manage.",
              "Manage dialog now contains the other settings.",
              "Onboarding screens added.",
              "Bug fixes."
            ]
          },
    {
          version: "4.0 iOS",
          changes: [
            "First release of SpotEQ31 now available on iOS!",
            "Works similarly to the Android version, see feature comparison and FAQ" ,
          ]
        },
    {
      version: "3.2",
      changes: [
        "Visualizer now uses 300 ms average window.",
        "Subscribers can set 100-1600 ms visualizer window.",
        "Click a band dB value to also fine tune Hz, if using a custom EQ config",
        "Faster and improved UI",
        "Bug fixes."
      ]
    },
    {
      version: "3.1.1",
      changes: [
          "Bug fixes."
      ]
    },
    {
      version: "3.1",
      changes: [
        "Improved and restructured UI.",
        "Create media/podcast playlists (3 free ones).",
        "Each playlist now can have up to 8 free tracks.",
        "Ads are removed by unlocking the Basic features or Subscription.",
        "Bug fixes."
      ]
    },
    {
      version: "3.0",
      changes: [
        "The Manage Presets & Bands view has been updated with clearer functionality.",
        "A new function to automatically connect a headset to a preset, can be found in the Manage view.",
        "New buttons and UI coloring.",
        "Refactoring and bug fixes."
      ]
    },
    {
      version: "2.2",
      changes: [
        "Added a new effect, Auto-pan, which sweeps sound from left to right at a set pace.",
        "Minor UI updates.",
        "Refactoring and bug fixes."
      ]
    },
    {
      version: "2.1",
      changes: [
        "App now tries to use the global sound output instead of scanning for audio separately, this might improve sound connection. If needed (not recommended) you can still activate background audio scanning from the Settings menu, please report any issues.",
        "To keep the app running in the background, battery permission is needed. The app will now notify if that is not yet granted.",
        "Bug fixes"
      ]
    },
    {
      version: "2.0",
      changes: [
        "dB values highlighted when slider moved",
        "Bug fixes"
      ]
    },
    {
      version: "1.16",
      changes: [
        "Built-in media player! You can now play local sound files, podcasts or web media directly from SpotEQ31. The player can be hidden from the Settings menu.",
        "New default color",
        "Bug fixes"
      ]
    },
    {
      version: "1.15",
      changes: [
        "Custom EQs: Users unlocking the Subscription or the Basic features can now create their own EQ config with custom frequencies. This can be found from the Manage button and Hz config.",
        "New export format",
        "Bug fixes"
      ]
    }
  ];

  return (
    <div style={{ padding: "40px 20px", maxWidth: "900px", margin: "0 auto", lineHeight: "1.6" }}>
      <section style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>About <span style={{ color: accentColor }}>SpotEQ31</span></h1>
        <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto' }}>
          SpotEQ31 is created by a small startup dedicated to providing the ultimate audio customization experience on Android and iOS.
        </p>
      </section>

      <div style={{ 
        padding: '30px', 
        borderRadius: '20px', 
        background: theme === 'dark' ? '#1a1a1a' : '#f0f4f8',
        marginBottom: '40px',
        boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <p>
          We believe that everyone deserves a perfect listening experience, regardless of their hardware or hearing ability. 
          Our mission is to bridge the gap between niche audiophile tools and everyday accessibility.
        </p>
        <p>
          SpotEQ31 started as a project to help users with uneven headphones and hearing impairments, 
          and has grown into a powerful tool for anyone who wants precise control over their device's sound.
        </p>
        <p>
          Our legal documents are maintained separately for each platform, since the in-app purchase, distribution and consent flows differ between the App Store and Google Play:
        </p>
        <ul>
          <li>
            <strong>Android:</strong> <Link to="/privacy" style={linkStyle}>Privacy Policy</Link> · <Link to="/terms" style={linkStyle}>Terms of Use</Link>
          </li>
          <li>
            <strong>iOS:</strong> <Link to="/privacyios" style={linkStyle}>Privacy Policy</Link> · <Link to="/termsios" style={linkStyle}>Terms of Use</Link>
          </li>
        </ul>
        <p>
          If you would like to reach us for questions or enquiries, please contact us at <a href="mailto:support@spoteq31.com" style={{ color: accentColor, textDecoration: 'none' }}>support@spoteq31.com</a>
          </p>
          <p>
          Android specific support <a href="mailto:android@spoteq31.com" style={{ color: accentColor, textDecoration: 'none' }}>android@spoteq31.com</a>
          </p>
          <p>
          iOS specific support <a href="mailto:ios@spoteq31.com" style={{ color: accentColor, textDecoration: 'none' }}>ios@spoteq31.com</a>

        </p>
        <p>
          This app is provided by LeanderCreative AB, Sweden.
        </p>
      </div>

      <section style={{ marginBottom: '60px' }}>
        <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>Version History</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {versionHistory.map((item, index) => (
            <div key={index} style={{ 
              padding: '20px', 
              borderRadius: '12px', 
              background: theme === 'dark' ? '#252525' : '#f9f9f9', 
              borderLeft: `5px solid ${accentColor}`,
              boxShadow: theme === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: accentColor }}>Version {item.version}</h3>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {item.changes.map((change, i) => (
                  <li key={i} style={{ marginBottom: '5px' }}>{change}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <footer style={{ fontSize: '0.8rem', opacity: 0.7, textAlign: 'center', borderTop: '1px solid #444', paddingTop: '20px', marginTop: '60px' }}>
        <p>Thank you for supporting independent software development.</p>
      </footer>

      <style>{`
        @media (max-width: 600px) {
          h1 {
            font-size: 2.5rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default About;
